<template>
  <transition name="slide-fade">
    <div
      v-if="visible"
      class="side-panel-overlay"
      @dblclick="closeOnOverlay"
    >
      <div class="side-panel">
        <validation-observer ref="taskFormRef">
          <!-- Header -->
          <div class="side-panel-header">
            <div class="d-flex w-100 justify-content-between align-items-start">
              <div
                class="pr-2"
                :class="{ 'w-100': isEditMode }"
              >
                <div class="d-flex w-100 align-items-center">
                  <validation-provider
                    v-if="isEditMode"
                    #default="{ errors }"
                    name="title"
                    class="flex-grow-1"
                    rules="required"
                  >
                    <b-form-input
                      id="task-title"
                      v-model="form.title"
                      :placeholder="$t('Enter task title')"
                      size="lg"
                      required
                      autofocus
                      autocomplete="off"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                  <div
                    v-else
                    class="d-flex align-items-center"
                  >
                    <h4 class="mb-0 mr-1 panel-title">
                      {{ task ? task.title : $t("New Task") }}
                    </h4>

                    <b-badge
                      v-b-tooltip.hover="linkCopied ? 'Copied!' : 'Copy Share Link'"
                      :variant="linkCopied ? 'success' : 'warning'"
                      class="cursor-pointer"
                      @click="copyShareLink"
                    >
                      <feather-icon icon="LinkIcon" />
                    </b-badge>
                  </div>

                </div>
                <span
                  v-if="task"
                  class="task-id"
                >#{{ task.id || task.uuid?.substring(0, 8) }}</span>
                <span v-if="!isEditMode && task && task.labels?.length">
                  <b-badge
                    v-for="label in task.labels"
                    :key="label.uuid"
                    :style="{ backgroundColor: label.color, marginLeft: '8px' }"
                  >
                    {{ label.name }}
                  </b-badge>
                </span>
              </div>
              <div class="d-flex align-items-center gap-2">
                <b-button
                  v-if="task && !isEditMode"
                  variant="primary"
                  style="width: 100px"
                  @click="enterEditMode"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                    class="mr-50"
                  />
                  {{ $t("Edit") }}
                </b-button>
                <b-button
                  variant="link"
                  class="close-btn"
                  @click="onModalClose"
                >
                  <feather-icon
                    icon="XIcon"
                    size="20"
                  />
                </b-button>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="side-panel-content">
            <b-form @submit.prevent="onSubmit">
              <b-row>
                <!-- Main Content -->
                <b-col
                  md="8"
                  sm="12"
                >
                  <!-- View Mode: Description Display -->
                  <div
                    v-if="!isEditMode && task"
                    class="mb-3"
                  >
                    <div
                      class="border border-2 rounded-lg p-1"
                      @click="onMarkdownClick"
                    >
                      <markdown-it-vue
                        class="markdown-body"
                        :options="options"
                        :content="processMentionContent(form.description, GET_CURRENT_BOARD?.members?.data || [])"
                      />
                    </div>
                  </div>

                  <!-- Edit Mode: Description Editor -->
                  <div
                    v-if="isEditMode"
                    class="border border-2 rounded-lg p-1"
                  >
                    <b-form-group
                      :label="$t('Description')"
                      label-for="task-description"
                      class="mb-3"
                    >
                      <markdown-editor
                        id="task-description"
                        v-model="form.description"
                        :placeholder="$t('Add description...')"
                        :rows="18"
                        :task-uid="task?.uuid"
                        :board-members="boardMembers"
                      />
                    </b-form-group>
                  </div>

                  <!-- Comments -->
                  <task-comments
                    v-if="!isEditMode"
                    @close-confirmed="enterEditMode"
                  />
                </b-col>

                <!-- Sidebar -->
                <b-col
                  md="4"
                  sm="12"
                >
                  <!-- View Mode: Status, Assignee & Due Date Display -->
                  <div v-if="!isEditMode && task">
                    <div class="task-metadata mb-3">
                      <div class="metadata-item">
                        <label class="metadata-label">{{ $t("Assignee") }}</label>
                        <div class="metadata-value">
                          <div
                            v-if="form.assigneeUid"
                            class="assignee-display"
                          >
                            <b-avatar
                              :src="getStorage(getAssignee?.avatar)"
                              :text="getInitials(getAssignee?.name)"
                              size="24"
                              :variant="'light-primary'"
                            />
                            <span class="ml-2">{{ getAssignee?.name }}</span>
                          </div>
                          <span
                            v-else
                            class="text-muted"
                          >{{
                            $t("Unassigned")
                          }}</span>
                        </div>
                      </div>
                      <div class="metadata-item">
                        <label class="metadata-label">{{ $t("Status") }}</label>
                        <div class="metadata-value">
                          <span
                            class="status-badge"
                            :style="{
                              backgroundColor: getStatusColor(form.statusId),
                            }"
                          >
                            {{ getStatusName(form.statusId) }}
                          </span>
                        </div>
                      </div>
                      <div class="metadata-item">
                        <label class="metadata-label">{{ $t("Priority") }}</label>
                        <div class="metadata-value text-capitalize">
                          <div
                            v-if="form.priority"
                            class="d-flex align-items-center"
                          >
                            <feather-icon
                              icon="CircleIcon"
                              stroke-width="5"
                              :class="`text-${priorityOptions.find(p => p.value === form.priority)?.color || 'secondary'}`"
                            />
                            <p class="mb-0 ml-1">
                              {{ form.priority.toLowerCase() }}
                            </p>
                          </div>
                          <span
                            v-else
                            class="text-muted"
                          >{{
                            $t("No priority")
                          }}</span>
                        </div>
                      </div>
                      <div class="metadata-item">
                        <label class="metadata-label">{{ $t("Due Date") }}</label>
                        <div class="metadata-value">
                          <span v-if="form.dueDate">{{
                            moment(form.dueDate).format("MMM D, YYYY")
                          }}</span>
                          <span
                            v-else
                            class="text-muted"
                          >{{
                            $t("No due date")
                          }}</span>
                        </div>
                      </div>

                      <div class="metadata-item">
                        <label class="metadata-label">{{ $t("Time") }}</label>
                        <div class="metadata-value">
                          <span
                            v-if="form.timeEstimateValue && form.timeEstimate"
                            class="estimate-chip"
                          >
                            {{ form.timeEstimateValue }}{{ { minutes: 'm', hours: 'h', days: 'd', weeks: 'w' }[form.timeEstimate] }}
                          </span>
                          <span
                            v-else
                            class="text-muted"
                          >{{ $t("No estimate") }}</span>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="task.milestone"
                      class="task-metadata mb-3"
                    >
                      <div
                        v-b-toggle.task-milestone
                        class="metadata-item justify-content-between cursor-pointer"
                      >
                        <div class="d-flex">
                          <label class="metadata-label">{{
                            $t("Milestone")
                          }}</label>

                          <div class="metadata-value">
                            <span v-if="task.milestone">
                              {{ task.milestone.name }}
                            </span>
                          </div>
                        </div>

                        <feather-icon
                          :icon="
                            collapseShown ? 'ChevronUpIcon' : 'ChevronDownIcon'
                          "
                          size="16"
                          class="ml-1"
                        />
                      </div>

                      <b-collapse
                        id="task-milestone"
                        class="mt-2"
                        @input="collapseShown = $event"
                      >
                        <span>{{ task.milestone.description }}</span>
                        <b-badge
                          :variant="
                            isBeforeTODAY(task.milestone.deadline)
                              ? 'light-danger'
                              : 'light-secondary'
                          "
                          class="d-flex mt-2 mr-50 w-50"
                        >
                          <feather-icon
                            icon="CalendarIcon"
                            size="10"
                            class="mr-25"
                          />
                          {{
                            FORMAT_DATE(task.milestone.deadline, "MMM DD, YYYY")
                          }}
                        </b-badge>
                      </b-collapse>
                    </div>

                    <task-activities :activities="task.activities" />
                  </div>

                  <!-- Edit Mode: Assignee, Labels, Status & Due Date Fields -->
                  <div
                    v-else
                    class="mb-3"
                  >
                    <b-form-group
                      :label="$t('Assignee')"
                      label-for="task-assignee"
                      class="mb-2"
                    >
                      <assignee-selector
                        v-model="form.assigneeUid"
                        :members="boardMembers"
                        :multiple="false"
                      />
                    </b-form-group>

                    <b-form-group
                      v-if="isEditMode"
                      :label="$t('Labels')"
                      label-for="task-label"
                      class="mb-2"
                    >
                      <v-select
                        v-model="form.labelUids"
                        :options="GET_CURRENT_BOARD_LABELS"
                        label="name"
                        :reduce="(r) => r.uuid"
                        :clearable="false"
                        :placeholder="$t('Select Labels')"
                        multiple
                      >
                        <template
                          #selected-option-container="{ option, deselect }"
                        >
                          <div
                            class="vs__selected"
                            :style="{ backgroundColor: option.color }"
                          >
                            <span class="text-white font-weight-bold">{{
                              option.name
                            }}</span>
                            <feather-icon
                              icon="XIcon"
                              size="12"
                              class="text-secondary vs__deselect"
                              @click="deselect(option)"
                            />
                          </div>
                        </template>

                        <template #option="{ name, color }">
                          <b-badge :style="{ backgroundColor: color }">
                            {{ name }}
                          </b-badge>
                        </template>
                      </v-select>
                    </b-form-group>

                    <b-form-group
                      :label="$t('Status')"
                      label-for="task-status"
                      class="mb-2"
                    >
                      <v-select
                        v-model="form.statusId"
                        :options="board.statuses"
                        label="name"
                        :reduce="(r) => r.id"
                        :clearable="false"
                      >
                        <template #option="{ name, color, isFinal }">
                          <div
                            class="status-info d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <span
                                class="status-dot"
                                :style="{ backgroundColor: color || '#94a3b8' }"
                              />
                              <span>{{ name }}</span>
                            </div>
                            <feather-icon
                              v-if="isFinal"
                              icon="CheckCircleIcon"
                              size="14"
                              class="mr-2 text-success"
                            />
                          </div>
                        </template>

                        <template #selected-option="{ name, color, isFinal }">
                          <div class="status-info">
                            <span
                              class="status-dot"
                              :style="{ backgroundColor: color || '#94a3b8' }"
                            />
                            <span>{{ name }}</span>
                            <feather-icon
                              v-if="isFinal"
                              icon="CheckCircleIcon"
                              size="14"
                              class="ml-1 text-success"
                            />
                          </div>
                        </template>
                      </v-select>
                    </b-form-group>

                    <b-form-group
                      :label="$t('Priority')"
                      label-for="task-priority"
                      class="mb-2"
                    >
                      <v-select
                        v-model="form.priority"
                        :options="priorityOptions"
                        label="text"
                        :reduce="(r) => r.value"
                        :clearable="false"
                        :placeholder="$t('Select Task Priority')"
                      >
                        <template #selected-option="{ text, description, color }">
                          <div class="d-flex align-items-center">
                            <feather-icon
                              icon="CircleIcon"
                              stroke-width="5"
                              :class="`text-${color}`"
                            />
                            <span class="ml-1 d-flex flex-column">{{ text }}</span>
                          </div>
                        </template>

                        <template #option="{ text, description, color }">
                          <div class="d-flex align-items-center">
                            <feather-icon
                              icon="CircleIcon"
                              stroke-width="5"
                              :class="`text-${color}`"
                            />
                            <div class="ml-1 d-flex flex-column">
                              <span>{{ text }}</span>
                              <small class="text-secondary">{{ description }}</small>
                            </div>
                          </div>
                        </template>
                      </v-select>
                    </b-form-group>

                    <b-form-group
                      :label="$t('Due Date')"
                      label-for="task-due-date"
                      class="mb-2"
                    >
                      <b-form-datepicker
                        id="task-due-date"
                        v-model="form.dueDate"
                        :placeholder="$t('Select Due Date')"
                        :date-format-options="{
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                        }"
                        locale="en"
                        reset-button
                      />
                    </b-form-group>

                    <b-form-group
                      :label="$t('Time Estimate')"
                      label-for="task-time-estimate"
                      class="mb-2"
                    >
                      <b-input-group
                        id="task-time-estimate"
                        class="time-estimate-group"
                      >
                        <b-form-input
                          v-model.number="form.timeEstimateValue"
                          type="number"
                          :min="form.timeEstimate === 'minutes' ? 1 : 0.5"
                          :step="form.timeEstimate === 'minutes' ? 1 : 0.5"
                          placeholder="—"
                          @input="!form.timeEstimate ? form.timeEstimate = 'hours' : null"
                        />
                        <b-input-group-append>
                          <b-dropdown
                            :text="{ minutes: $t('m'), hours: $t('h'), days: $t('d'), weeks: $t('w') }[form.timeEstimate] || $t('h')"
                            variant="outline-secondary"
                            toggle-class="time-estimate-unit"
                            right
                          >
                            <b-dropdown-item @click="form.timeEstimate = 'minutes'">
                              {{ $t('Minutes') }}
                            </b-dropdown-item>
                            <b-dropdown-item @click="form.timeEstimate = 'hours'">
                              {{ $t('Hours') }}
                            </b-dropdown-item>
                            <b-dropdown-item @click="form.timeEstimate = 'days'">
                              {{ $t('Days') }} <small class="text-muted ml-1">8h</small>
                            </b-dropdown-item>
                            <b-dropdown-item @click="form.timeEstimate = 'weeks'">
                              {{ $t('Weeks') }} <small class="text-muted ml-1">5d</small>
                            </b-dropdown-item>
                          </b-dropdown>
                        </b-input-group-append>
                        <b-input-group-append v-if="form.timeEstimateValue">
                          <b-button
                            variant="outline-secondary"
                            class="time-estimate-clear"
                            @click="form.timeEstimateValue = null; form.timeEstimate = null"
                          >
                            <feather-icon
                              icon="XIcon"
                              size="12"
                            />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>

                      <!-- Quick select presets -->
                      <div class="time-presets mt-50 d-flex flex-wrap align-items-center">
                        <span
                          v-for="preset in presetEstimates"
                          :key="preset.label"
                          class="preset-chip mr-25 mb-25 cursor-pointer"
                          :class="{ 'active': isPresetSelected(preset) }"
                          @click="selectPreset(preset)"
                        >
                          {{ preset.label }}
                        </span>
                      </div>

                      <!-- Live breakdown summary -->
                      <transition name="fade">
                        <div
                          v-if="timeEstimateSummary"
                          class="time-estimate-summary mt-50 d-flex align-items-center"
                        >
                          <feather-icon
                            icon="ClockIcon"
                            size="12"
                            class="mr-25 text-primary"
                          />
                          <span>{{ timeEstimateSummary }}</span>
                        </div>
                      </transition>
                    </b-form-group>

                    <b-form-group
                      :label="$t('Milestone')"
                      label-for="task-milestone"
                      class="mb-2"
                    >
                      <v-select
                        id="task-milestone"
                        v-model="form.milestoneUid"
                        :options="board.milestones?.data || []"
                        label="name"
                        :reduce="(r) => r.uuid"
                        :clearable="false"
                        :placeholder="$t('Select Milestone')"
                      />
                    </b-form-group>
                  </div>
                </b-col>
              </b-row>

              <div class="side-panel-actions">
                <div class="d-flex justify-content-between align-items-center">
                  <b-button
                    variant="danger"
                    @click="onModalClose"
                  >
                    {{ isEditMode ? $t("Cancel") : $t("Close") }}
                  </b-button>
                  <b-button
                    v-if="isEditMode"
                    type="submit"
                    variant="primary"
                    :disabled="GET_PROCESSING"
                  >
                    <b-spinner
                      v-if="GET_PROCESSING"
                      small
                      class="mr-50"
                    />
                    {{ task ? $t("Update Task") : $t("Create Task") }}
                  </b-button>
                </div>
              </div>
            </b-form>
          </div>
        </validation-observer>
      </div>

      <CoolLightBox
        :items="fetchImages || []"
        :index="coolLightBoxIndex"
        @close="coolLightBoxIndex = null"
      />
    </div>
  </transition>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormDatepicker,
  BCollapse,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BBadge,
  BAvatar,
  VBToggle,
  BInputGroup,
  BInputGroupAppend,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import MarkdownItVue from 'markdown-it-vue'
import CoolLightBox from 'vue-cool-lightbox'
import { mapGetters } from 'vuex'
import moment from 'moment'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { min_value } from 'vee-validate/dist/rules'
import AssigneeSelector from './AssigneeSelector.vue'
import MarkdownEditor from './MarkdownEditor.vue'
import parseMarkdown, { processMentionContent, fetchImagesFromMarkdown, removeNameFromMentions } from './markdown'
import TaskActivities from './TaskActivities/Index.vue'
import TaskComments from './components/TaskComments.vue'
import { priorityOptions } from '../useProjectManagement'

export default {
  name: 'TaskSidePanel',
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BDropdown,
    BDropdownItem,
    BCollapse,
    BFormDatepicker,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BBadge,
    BAvatar,
    AssigneeSelector,
    MarkdownEditor,
    MarkdownItVue,
    vSelect,
    CoolLightBox,
    TaskActivities,
    TaskComments,
    ValidationObserver,
    ValidationProvider,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    task: {
      type: Object,
      default: null,
    },
    board: {
      type: Object,
      required: true,
    },
    statusId: {
      type: [String, Number],
      default: null,
    },
    editingMode: {
      type: Boolean,
      default: false,
    },
    editingFromTaskCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      min_value,
      moment,
      priorityOptions,
      parseMarkdown,
      removeNameFromMentions,
      processMentionContent,
      form: {
        title: '',
        description: '',
        statusId: null,
        dueDate: null,
        assigneeUid: null,
        labelUids: [],
        priority: 'MEDIUM',
        milestoneUid: null,
        timeEstimate: null,
        timeEstimateValue: null,
      },
      isEditMode: false,
      collapseShown: false,
      options: {
        markdownIt: {
          linkify: true,
          html: true,
        },
        linkAttributes: {
          attrs: {
            target: '_blank',
            rel: 'noopener',
          },
        },
        image: {
          viewer: false,
        },
      },
      coolLightBoxIndex: null,
      uploadingFile: false,
      linkCopied: false,
      discardCommentAction: 'close-modal',
      presetEstimates: [
        { label: '15m', value: 15, unit: 'minutes' },
        { label: '30m', value: 30, unit: 'minutes' },
        { label: '1h', value: 1, unit: 'hours' },
        { label: '2h', value: 2, unit: 'hours' },
        { label: '4h', value: 4, unit: 'hours' },
        { label: '1d', value: 1, unit: 'days' },
        { label: '2d', value: 2, unit: 'days' },
        { label: '3d', value: 3, unit: 'days' },
        { label: '5d', value: 5, unit: 'days' },
      ],
    }
  },
  computed: {
    ...mapGetters('boards', [
      'GET_CURRENT_BOARD',
      'GET_CURRENT_BOARD_LABELS',
      'GET_PROCESSING',
      'GET_LOADING',
      'GET_CURRENT_COMMENT',
    ]),
    timeEstimateSummary() {
      const value = this.form.timeEstimateValue
      const unit = this.form.timeEstimate
      if (!value || value <= 0 || !unit) return ''

      if (unit === 'minutes') {
        if (value < 60) {
          return `${value} ${value === 1 ? this.$t('minute') : this.$t('minutes')}`
        }
        const hours = Math.floor(value / 60)
        const mins = value % 60
        if (mins === 0) {
          return `${hours} ${hours === 1 ? this.$t('hour') : this.$t('hours')}`
        }
        return `${hours} ${hours === 1 ? this.$t('hour') : this.$t('hours')} ${mins} ${mins === 1 ? this.$t('minute') : this.$t('minutes')}`
      }

      if (unit === 'hours') {
        const hours = value
        const days = Math.floor(hours / 8)
        const remainingHours = hours % 8
        if (days > 0) {
          if (remainingHours === 0) {
            return `${days} ${days === 1 ? this.$t('day') : this.$t('days')} (${hours} ${this.$t('hours')})`
          }
          const hoursPart = remainingHours === 1 ? this.$t('hour') : this.$t('hours')
          const daysPart = days === 1 ? this.$t('day') : this.$t('days')
          return `${days} ${daysPart} ${remainingHours} ${hoursPart} (${hours} ${this.$t('hours')})`
        }
        return `${hours} ${hours === 1 ? this.$t('hour') : this.$t('hours')}`
      }

      if (unit === 'days') {
        const totalHours = value * 8
        const days = value
        return `${days} ${days === 1 ? this.$t('day') : this.$t('days')} (${totalHours} ${this.$t('hours')})`
      }

      if (unit === 'weeks') {
        const totalDays = value * 5
        const totalHours = value * 40
        const weeks = value
        const weeksPart = weeks === 1 ? this.$t('week') : this.$t('weeks')
        const daysPart = totalDays === 1 ? this.$t('day') : this.$t('days')
        return `${weeks} ${weeksPart} (${totalDays} ${daysPart} / ${totalHours} ${this.$t('hours')})`
      }

      return ''
    },
    statusOptions() {
      return (this.board?.statuses || []).map(s => ({
        value: s.id,
        text: s.name,
      }))
    },
    boardMembers() {
      return this.board?.members?.data || []
    },
    getAssignee() {
      return this.boardMembers.find(m => m.uuid === this.form.assigneeUid)
    },
    fetchImages() {
      if (this.task) {
        return fetchImagesFromMarkdown(this.task.description)
      }
      return []
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.initForm()
        // Prevent body scroll when panel is open
        document.body.style.overflow = 'hidden'
      } else {
        // Restore body scroll when panel is closed
        document.body.style.overflow = ''
      }
    },
    task: {
      immediate: true,
      handler() {
        this.initForm()
      },
    },
    editingMode: {
      immediate: true,
      handler(val) {
        this.isEditMode = val
      },
    },
  },
  beforeDestroy() {
    // Ensure body scroll is restored
    document.body.style.overflow = ''
  },
  methods: {
    minutesToEstimate(minutes) {
      if (!minutes) return { unit: null, value: null }
      const WEEK = 60 * 8 * 5
      const DAY = 60 * 8
      if (minutes < 60) return { unit: 'minutes', value: minutes }
      if (minutes % WEEK === 0) return { unit: 'weeks', value: minutes / WEEK }
      if (minutes % DAY === 0) return { unit: 'days', value: minutes / DAY }
      return { unit: 'hours', value: +(minutes / 60).toFixed(2) }
    },
    isPresetSelected(preset) {
      return this.form.timeEstimate === preset.unit && this.form.timeEstimateValue === preset.value
    },
    selectPreset(preset) {
      this.form.timeEstimate = preset.unit
      this.form.timeEstimateValue = preset.value
    },
    handleDiscardComment() {
      if (this.discardCommentAction === 'enter-edit-mode') {
        this.enterEditMode()
      } else this.handleCancel()
    },
    initForm() {
      if (this.task) {
        this.form = {
          title: this.task.title || '',
          description: this.task.description || '',
          statusId: this.task.status?.id || this.statusId,
          dueDate: this.task.dueDate || null,
          assigneeUid: this.task.assignee?.uuid || null,
          labelUids: this.task.labels?.map(label => label.uuid) || [],
          milestoneUid: this.task.milestone?.uuid || null,
          priority: this.task.priority || 'MEDIUM',
          timeEstimate: this.minutesToEstimate(this.task.timeEstimate).unit,
          timeEstimateValue: this.minutesToEstimate(this.task.timeEstimate).value,
        }
      } else {
        this.form = {
          title: '',
          description: '',
          statusId: this.statusId || this.statusOptions[0]?.value || null,
          dueDate: null,
          assigneeUid: null,
          milestoneUid: this.$route.params.milestoneUid || null,
          labelUids: [],
          priority: 'MEDIUM',
          timeEstimate: null,
          timeEstimateValue: null,
        }
      }
    },
    enterEditMode() {
      if (this.GET_CURRENT_COMMENT) {
        this.discardCommentAction = 'enter-edit-mode'
        this.$bvModal.show('close-confirmation-modal')
      } else {
        this.isEditMode = true
        this.$emit('enter-edit-mode')
      }
    },
    handleCancel() {
      if (this.GET_CURRENT_COMMENT) this.$bvModal.show('close-confirmation-modal')
      else {
        this.isEditMode = false
        this.initForm() // Reset form to original values
      }
    },
    async onSubmit() {
      try {
        const validationResult = await this.$refs.taskFormRef.validate()
        if (!validationResult) return

        const { timeEstimate, timeEstimateValue } = this.form
        let totalTimeEstimateInMinutes = null

        if (timeEstimate && timeEstimateValue !== null && timeEstimateValue !== '') {
          const numValue = Number(timeEstimateValue)
          if (!isNaN(numValue)) {
            switch (timeEstimate) {
            case 'minutes':
              totalTimeEstimateInMinutes = Math.round(numValue)
              break
            case 'hours':
              totalTimeEstimateInMinutes = Math.round(numValue * 60)
              break
            case 'days':
              totalTimeEstimateInMinutes = Math.round(numValue * 60 * 8) // Assuming 8 hours in a workday
              break
            case 'weeks':
            case 'week':
              totalTimeEstimateInMinutes = Math.round(numValue * 60 * 8 * 5) // Assuming 5 workdays in a week
              break
            default:
              break
            }
          }
        }

        const payload = {
          title: this.form.title,
          description: removeNameFromMentions(this.form.description),
          statusId: this.form.statusId,
          dueDate: this.form.dueDate,
          projectUid: this.board?.project?.uuid,
          assigneeUid: this.form.assigneeUid,
          labelUids: this.form.labelUids,
          priority: this.form.priority,
          milestoneUid: this.form.milestoneUid,
        }

        if (Number.isInteger(totalTimeEstimateInMinutes) && totalTimeEstimateInMinutes >= 0) {
          payload.timeEstimate = totalTimeEstimateInMinutes
        }

        this.$emit('submit', payload)
      } catch (err) {
        console.error('Error during onSubmit:', err)
      }
    },
    getStatusName(statusId) {
      return this.board?.statuses?.find(s => s.id === statusId)?.name || ''
    },
    getStatusColor(statusId) {
      return (
        this.board?.statuses?.find(s => s.id === statusId)?.color || '#e1e4e8'
      )
    },
    close() {
      this.$emit('close')
      window.history.pushState(null, '', `/project-management/board/${this.base58Converter(this.board?.uuid)}`)
    },
    closeOnOverlay(e) {
      if (e.target.classList.contains('side-panel-overlay')) {
        this.close()
      }
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    onMarkdownClick({ target }) {
      if (target.tagName === 'IMG') {
        const src = target.getAttribute('src')
        const index = this.fetchImages.findIndex(img => img === src)

        if (index !== -1) {
          this.coolLightBoxIndex = index
        }
      }
    },
    copyShareLink() {
      const boardUid = this.base58Converter(this.board?.uuid)
      const taskUid = this.base58Converter(this.task?.uuid)

      const shareLink = `${window.location.origin}/project-management/board/${boardUid}/tasks/${taskUid}`
      navigator.clipboard.writeText(shareLink).then(() => {
        this.linkCopied = true
      })
      setTimeout(() => {
        this.linkCopied = false
      }, 2000)
    },
    onModalClose() {
      if (this.editingFromTaskCard) {
        this.close()
      } else if (this.isEditMode) {
        this.handleCancel()
      } else {
        this.close()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-side-panel.scss";

.time-estimate-group {
  .form-control {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    max-width: 90px;

    &:focus {
      z-index: 3;
    }
  }

  .time-estimate-unit {
    border-radius: 0;
    border-left: none;
    border-right: none;
    min-width: 44px;
    font-weight: 600;
    font-size: 0.85rem;
  }

  .time-estimate-clear {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
    padding: 0 8px;
    color: #aaa;

    &:hover {
      color: #dc3545;
    }
  }
}

.estimate-chip {
  display: inline-block;
  background: rgba(var(--primary-rgb, 115, 103, 240), 0.1);
  color: var(--primary, #7367f0);
  font-weight: 600;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 20px;
}

.time-presets {
  margin-top: 8px;
}

.preset-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 12px;
  background-color: #f3f2f7;
  color: #5e5873;
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
  user-select: none;

  &:hover {
    background-color: #e8e7ed;
    transform: translateY(-1px);
  }

  &.active {
    background-color: rgba(115, 103, 240, 0.12);
    color: #7367f0;
    border-color: rgba(115, 103, 240, 0.3);
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(115, 103, 240, 0.1);
  }
}

.time-estimate-summary {
  font-size: 0.8rem;
  color: #6e6b7b;
  background-color: #f8f8f8;
  padding: 6px 10px;
  border-radius: 6px;
  border-left: 2px solid #7367f0;
}
</style>

<style lang="scss">
.markdown-body img {
  cursor: pointer;
}
</style>
